import { useState, useCallback, useRef } from 'react';
import type {
  ChatMessage,
  SSEEvent,
  ToolUse,
  VisualizationData,
  Citation,
  ContentBlock,
} from '../types';
import { generateUUID } from '../lib/uuid';
import { nebulaFetch } from '../lib/nebulaClient';
import { fetchConversation, transformAPIMessagesToChatMessages } from '../lib/api';
import { trackEvent, trackActivationMilestone, NEBULA_EVENTS, ACTIVATION_EVENTS } from '../../services/analytics';
import { auth } from '../../config/firebase';

interface UseChatOptions {
  conversationId?: string;
  onConversationIdChange?: (id: string) => void;
  onConversationUpdate?: () => void;
}

export function useChat(options: UseChatOptions = {}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | undefined>(
    options.conversationId
  );

  const abortControllerRef = useRef<AbortController | null>(null);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading) return;

      // Track message sent
      trackEvent(NEBULA_EVENTS.NEBULA_MESSAGE_SENT, {
        message_length: content.length,
      });

      // Track first Nebula query as activation milestone
      const userId = auth.currentUser?.uid;
      if (userId) {
        trackActivationMilestone(ACTIVATION_EVENTS.FIRST_NEBULA_QUERY, userId);
      }

      // Cancel any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      setError(null);

      // Add user message
      const userMessage: ChatMessage = {
        id: generateUUID(),
        role: 'user',
        content,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      // Create assistant message placeholder
      const assistantId = generateUUID();
      const assistantMessage: ChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        contentBlocks: [],
        timestamp: new Date(),
        isStreaming: true,
        toolUses: [],
      };
      setMessages((prev) => [...prev, assistantMessage]);

      try {
        // Build URL with conversation ID if available
        let url = `/api/v1/chat?message=${encodeURIComponent(content)}`;
        if (conversationId) {
          url += `&conversationId=${encodeURIComponent(conversationId)}`;
        }

        const response = await nebulaFetch(url, {
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error('Chat request failed');
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error('No response body');
        }

        let accumulatedContent = '';
        let thinkingContent = '';
        let currentToolUses: ToolUse[] = [];
        let contentBlocks: ContentBlock[] = [];
        let citations: Citation[] = [];

        // Helper to update the message
        const updateMessage = (updates: Partial<ChatMessage>) => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantId ? { ...msg, ...updates } : msg
            )
          );
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const event: SSEEvent = JSON.parse(line.slice(6));

                switch (event.type) {
                  case 'thinking':
                    if (event.content) {
                      thinkingContent += event.content;
                      updateMessage({ thinkingContent, isStreaming: true });
                    }
                    break;

                  case 'text':
                    if (event.content) {
                      accumulatedContent += event.content;
                      const lastBlock = contentBlocks[contentBlocks.length - 1];
                      if (lastBlock && lastBlock.type === 'text') {
                        lastBlock.content += event.content;
                        contentBlocks = [...contentBlocks.slice(0, -1), lastBlock];
                      } else {
                        contentBlocks = [
                          ...contentBlocks,
                          { type: 'text', content: event.content },
                        ];
                      }
                      updateMessage({ content: accumulatedContent, contentBlocks });
                    }
                    break;

                  case 'tool_use':
                    if (event.data) {
                      const toolData = event.data as {
                        tool: string;
                        input?: unknown;
                        status?: string;
                      };
                      const toolId = `tool_${Date.now()}`;

                      const existingIndex = currentToolUses.findIndex(
                        (t) => t.tool === toolData.tool && t.status === 'running'
                      );

                      if (existingIndex >= 0) {
                        currentToolUses[existingIndex] = {
                          ...currentToolUses[existingIndex],
                          status:
                            (toolData.status as ToolUse['status']) || 'running',
                        };
                      } else {
                        currentToolUses.push({
                          id: toolId,
                          tool: toolData.tool,
                          input: toolData.input,
                          status: 'running',
                        });
                      }

                      updateMessage({ toolUses: [...currentToolUses] });
                    }
                    break;

                  case 'tool_result':
                    if (currentToolUses.length > 0) {
                      currentToolUses[currentToolUses.length - 1].status =
                        'complete';
                      updateMessage({ toolUses: [...currentToolUses] });
                    }
                    break;

                  case 'visualization':
                    if (event.data) {
                      const vizData = event.data as VisualizationData;
                      contentBlocks = [
                        ...contentBlocks,
                        {
                          type: 'visualization',
                          data: vizData,
                        },
                      ];
                      updateMessage({ contentBlocks });

                      // Track visualization generated
                      trackEvent(NEBULA_EVENTS.NEBULA_VISUALIZATION_GENERATED, {
                        viz_type: vizData.config?.type || 'unknown',
                      });
                    }
                    break;

                  case 'citations':
                    if (event.data) {
                      citations = event.data as Citation[];
                      updateMessage({ citations });
                    }
                    break;

                  case 'error':
                    setError(event.content || 'An error occurred');
                    break;

                  case 'done':
                    if (event.data) {
                      const doneData = event.data as { conversationId?: string };
                      if (doneData.conversationId) {
                        setConversationId(doneData.conversationId);
                        options.onConversationIdChange?.(doneData.conversationId);
                        options.onConversationUpdate?.();
                      }
                    }
                    currentToolUses = currentToolUses.map((t) => ({
                      ...t,
                      status: 'complete' as const,
                    }));
                    updateMessage({
                      isStreaming: false,
                      toolUses: currentToolUses,
                    });
                    break;
                }
              } catch {
                // Skip invalid JSON lines
              }
            }
          }
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        setError(err instanceof Error ? err.message : 'An error occurred');
        setMessages((prev) => prev.filter((msg) => msg.id !== assistantId));
      } finally {
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    },
    [isLoading, conversationId, options]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
    setConversationId(undefined);
  }, []);

  const stopGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  }, []);

  const loadConversation = useCallback(
    async (id: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const conversation = await fetchConversation(id);
        const chatMessages = transformAPIMessagesToChatMessages(conversation.messages);

        setMessages(chatMessages);
        setConversationId(id);
        options.onConversationIdChange?.(id);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load conversation');
      } finally {
        setIsLoading(false);
      }
    },
    [options]
  );

  return {
    messages,
    isLoading,
    error,
    conversationId,
    sendMessage,
    clearMessages,
    stopGeneration,
    loadConversation,
  };
}
